import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HardHat, Warehouse, Tractor, Truck, Snowflake, Factory, ArrowRight } from 'lucide-react';

const industries = [
  {
    icon: HardHat,
    title: 'Construction',
    slug: '/industries/construction',
    desc: 'General labourers, framers, concrete workers, demolition crews, and skilled tradespeople for residential and commercial projects across BC.',
    gradient: 'from-amber-500 to-orange-600',
  },
  {
    icon: Warehouse,
    title: 'Warehouse & Fulfillment',
    slug: '/industries/warehouse',
    desc: 'Pickers, packers, forklift operators, shipping/receiving clerks, and inventory management for fulfillment centres and distribution hubs.',
    gradient: 'from-blue-500 to-blue-700',
  },
  {
    icon: Factory,
    title: 'Food Processing',
    slug: '/industries/food-processing',
    desc: 'Production workers, forklift operators, cleaning & sanitation crews, and drivers for food processing and packaging facilities.',
    gradient: 'from-rose-500 to-rose-700',
  },
  {
    icon: Tractor,
    title: 'Agriculture',
    slug: '/industries/agriculture',
    desc: 'Farm labourers, harvesters, greenhouse workers, and seasonal staff for BC\u2019s agricultural industry year-round.',
    gradient: 'from-green-500 to-green-700',
  },
  {
    icon: Truck,
    title: 'Transportation',
    slug: '/industries/transportation',
    desc: 'Drivers, dispatchers, loading dock workers, and logistics support personnel keeping BC\u2019s supply chain moving.',
    gradient: 'from-indigo-500 to-indigo-700',
  },
  {
    icon: Snowflake,
    title: 'Cold Storage',
    slug: '/industries/cold-storage',
    desc: 'Cold room operators, freezer workers, quality control staff, and food handling specialists for temperature-controlled facilities.',
    gradient: 'from-cyan-500 to-cyan-700',
  },
];

const IndustryOverview = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => { if (sectionRef.current) observer.unobserve(sectionRef.current); };
  }, []);

  return (
    <section ref={sectionRef} className="py-20 px-6 bg-white" data-testid="industry-overview" id="industries">
      <div className="max-w-7xl mx-auto">
        <div className={`text-center mb-14 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-[#003366] mb-4">Industries We Serve</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            WorkNation Labour Ltd provides specialized workforce solutions across six key industries in British Columbia. Need workers? We place qualified, WCB-compliant staff in 24-48 hours — so your operations never stop.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {industries.map((industry, index) => (
            <div
              key={index}
              onClick={() => navigate(industry.slug)}
              className={`group cursor-pointer bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-700 hover:shadow-2xl hover:-translate-y-1 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
              data-testid={`industry-card-${index}`}
              role="link"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && navigate(industry.slug)}
              aria-label={`Learn more about ${industry.title} staffing`}
            >
              <div className={`bg-gradient-to-br ${industry.gradient} p-6 text-white`}>
                <industry.icon size={40} className="mb-3 group-hover:scale-110 transition-transform duration-300" aria-hidden="true" />
                <h3 className="text-xl font-bold">{industry.title}</h3>
              </div>
              <div className="p-6">
                <p className="text-gray-700 leading-relaxed mb-4">{industry.desc}</p>
                <span className="inline-flex items-center text-[#003366] font-semibold group-hover:underline">
                  Learn More <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-200" size={16} />
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default IndustryOverview;
